import {Injectable} from '@angular/core';
import {
  HttpHeaders,
  HttpParameterCodec,
  HttpParams,
  HttpClient
} from '@angular/common/http';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {environment} from '../environments/environment';
import {Profile} from './admin.model';


// angular class encodes also the = char and because of that our auth with hash does not work
export class HttpUrlEncodingCodec implements HttpParameterCodec {
  private standardEncoding(v: string): string {
    return encodeURIComponent(v);
  }

  encodeKey(k: string): string {
    return this.standardEncoding(k);
  }

  encodeValue(v: string): string {
    return this.standardEncoding(v);
  }

  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  decodeValue(v: string) {
    return decodeURIComponent(v);
  }
}


@Injectable({providedIn: 'root'})
export class AdminService {
  private loggedIn = new BehaviorSubject<boolean>(false);
  private loggedInUserProfile = new BehaviorSubject<Profile | null>(null);

  constructor(private http: HttpClient) {
    this.isLoggedIn();
  }

  loggedInObservable() {
    return this.loggedIn.asObservable();
  }

  isLoggedIn() {
    try {
      const logStatus = !!localStorage.getItem('access_token');
      if (this.loggedIn.getValue() !== logStatus) {
        this.loggedIn.next(logStatus);
      }
      return logStatus;
    } catch (err) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      if (this.loggedIn.getValue()) {
        this.loggedIn.next(false);
      }
      return false;
    }
  }

  adminLogin(email: string, password: string) {
    const data = {
      grant_type: 'password',
      username: email,
      password
    };
    const reqParams = new HttpParams({fromObject: data, encoder: new HttpUrlEncodingCodec()});
    // remove the access token
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    return this.http.post(environment.clientApiV2Url + 'oauth/token', reqParams, {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${environment.aData}`,
      })
    }).pipe(tap((response: any) => {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('refresh_token', response.refresh_token);
        this.loggedIn.next(true);
      }),
      switchMap(() => {
        return new Observable(subscriber => {
          subscriber.next(null);
          subscriber.complete();
        });
      }),
      catchError(err => {
        return throwError(err);
      }));
  }

  refreshTokenLogin(refreshToken: string) {
    const data = {
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    };
    const reqParams = new HttpParams({fromObject: data, encoder: new HttpUrlEncodingCodec()});
    // remove the access token
    localStorage.removeItem('access_token');

    return this.http.post<{ access_token: string }>(environment.clientApiV2Url + 'oauth/token', reqParams, {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Basic ${environment.aData}`,
      }),
    }).pipe(tap((response: { access_token: string }) => {
        localStorage.setItem('access_token', response.access_token);
      }),
      catchError(err => {
        localStorage.removeItem('refresh_token');
        this.loggedIn.next(false);
        // TODO redirect to login page which we do not have
        return of(err);
      }));
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    this.loggedIn.next(false);
    this.loggedInUserProfile.next(null);
  }

  loadProfile() {
    return this.http.get<Profile>(
      environment.apiUrl + 'admin/profile').pipe(tap(userProfile => {
      this.loggedInUserProfile.next(userProfile);
    }));
  }

  getProfileAsObservable() {
    if (!this.loggedInUserProfile.getValue()) {
      if (this.isLoggedIn()) {
        // The user is logged in, load the profile to get the permissions.
        this.loadProfile().subscribe();
      }
    }
    return this.loggedInUserProfile.asObservable();
  }

  updateProfile(name: string, currentPassword: string, newPassword: string) {
    let data: any = {
      name
    };
    if (currentPassword && newPassword) {
      data['old_password'] = currentPassword;
      data['new_password'] = newPassword;
    }
    return this.http.post(
      environment.apiUrl + 'admin/profile', data).pipe(tap(() => {
        const profile = this.loggedInUserProfile.getValue();
        if (profile) {
          profile.name = name;
          this.loggedInUserProfile.next(profile);
        }
    }));
  }
}
